import { message, Upload, Button, UploadProps, UploadFile, Modal } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import { RcFile } from 'antd/lib/upload';
import { TextService } from '../../Services/TextService';


export enum UploadImageType {
    Logo = 0,
    Banner = 1,
    Public = 2,
}

export interface UploadImageProps {
    uploadImageType: UploadImageType;
    uploadFinish: (relativeUri: string) => void;
    crop?: boolean;
    // File names already in the picture archive. Uploads there keep their file name, so a file with
    // the same name replaces the existing image everywhere it is used - asked about before upload.
    existingFileNames?: string[];
    // Added before the file extension, e.g. "-banner" turns "party.jpg" into "party-banner.jpg", so a
    // cropped event banner does not replace the uncropped original in the picture archive.
    fileNameSuffix?: string;
}

export const addFileNameSuffix = (fileName: string, suffix?: string) => {
    if (!suffix) {
        return fileName;
    }
    const dotIndex = fileName.lastIndexOf(".");
    const baseName = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
    const extension = dotIndex > 0 ? fileName.substring(dotIndex) : "";
    return baseName.endsWith(suffix) ? fileName : baseName + suffix + extension;
}

// Picture archive uploads keep their file name, so asks before an existing image is replaced.
// Resolves true when there is nothing to replace or the user accepts.
export const confirmReplaceImage = (fileName: string, existingFileNames?: string[]): Promise<boolean> => {
    if (!existingFileNames?.includes(fileName)) {
        return Promise.resolve(true);
    }
    const GetText = TextService.GetText;
    return new Promise<boolean>(resolve => {
        Modal.confirm({
            title: GetText("UploadImage_ReplaceTitle"),
            content: GetText("UploadImage_ReplaceContent").replace("{0}", fileName),
            okText: GetText("Common_Yes"),
            cancelText: GetText("Common_No"),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

const UploadImage = (props: UploadImageProps) => {
    type NewType = UploadProps;
    const GetText = TextService.GetText;

    const onPreview = async (file: UploadFile) => {
        let src = file.url as string;
        if (!src) {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj as RcFile);
                reader.onload = () => resolve(reader.result as string);
            });
        }
        const image = new Image();
        image.src = src;
        const imgWindow = window.open(src);
        imgWindow?.document.write(image.outerHTML);
    };

    let uploadPath = process.env.REACT_APP_API_URL_BASE
    if (props.uploadImageType == UploadImageType.Public) {
        uploadPath += "/api/Image/uploadPublicImage"
    }
    else if (props.uploadImageType == UploadImageType.Banner) {
        uploadPath += "/api/Image/uploadImage"
    }
    else if (props.uploadImageType == UploadImageType.Logo) {
        uploadPath += "/api/Image/uploadLogo"
    }

    const uploadProps: NewType = {
        name: "file",
        action: uploadPath,
        method: 'POST',
        showUploadList: false,
        withCredentials: true,
        headers: {
            contentType: "multipart/form-data"
        },
        async onChange(info: any) {
            if (info.file.status === "done") {
                const { response } = info.file;
                props.uploadFinish(response.blob.relativeUri)
            } else if (info.file.status === "error") {
                message.error(GetText("UploadImage_Error"));
            }
        },
        onPreview: onPreview,
        beforeUpload(file: any) {
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
            if (!isJpgOrPng) {
                message.error(GetText("UploadImage_WrongFormat"));
            }
            const isLt2M = file.size / 1024 / 1024 < 1;
            const isGT20K = file.size / 1024 > 20;
            if (!isLt2M) {
                message.error(GetText("UploadImage_TooBig"));
            }
            if (!isJpgOrPng || !isLt2M) {
                return false;
            }
            // Resolving with a File makes antd upload that file instead - used to rename it.
            const fileName = addFileNameSuffix(file.name, props.fileNameSuffix);
            const fileToUpload = fileName === file.name ? file : new File([file], fileName, { type: file.type });
            return confirmReplaceImage(fileName, props.existingFileNames).then(accepted => {
                return accepted ? fileToUpload : Promise.reject();
            });
        }
    };

    return (
        props.crop ? <ImgCrop aspect={4 / 1}>
            <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>{GetText("UploadImage_UploadImageButton")}</Button>
            </Upload>
        </ImgCrop> : <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>{GetText("UploadImage_UploadImageButton")}</Button>
        </Upload>);
};

export default UploadImage;